import React, { useState, useEffect, useRef } from 'react';
import { FaDownload, FaCopy, FaCheck, FaSpinner, FaTimes } from 'react-icons/fa';

export default function CardCreatedModal({ isOpen, onClose, base64, cardParams, editConfig }) {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(true);
  const [uploadedId, setUploadedId] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const xhrRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !base64) return;

    setProgress(0);
    setIsUploading(true);
    setUploadedId('');
    setError('');
    setCopied(false);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;

    xhr.upload.addEventListener("progress", (evt) => {
      if (evt.lengthComputable) {
        const percentComplete = Math.round((evt.loaded / evt.total) * 100);
        setProgress(percentComplete);
      }
    }, false);

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const returnedId = xhr.responseText.trim();
        setUploadedId(returnedId);
        setIsUploading(false);

        // B. Perform redirect if action was 'edit' and redirect is active
        if (editConfig?.editMode && editConfig?.redirect === '1' && editConfig?.nid) {
          setTimeout(() => {
            window.location.href = `/gallery/?selectedCard=${editConfig.nid}`;
          }, 800); // 800ms brief delay so they see the success state
        }
      } else {
        setError('Server responded with an error.');
        setIsUploading(false);
      }
    };

    xhr.onerror = () => {
      setError('Connection to upload server failed.');
      setIsUploading(false);
    };

    xhr.open("POST", "/ajax/recieve.php", true);
    
    const formData = new FormData();

    // 1. Create a copy of cardParams and set Art to null
    // This removes all nested Art properties (Art.X, Art.blabla, etc.)
    const modifiedCardParams = {
      ...cardParams,
      Art: null
    };

    // 2. Append the modified object as JSON
    formData.append('cardParams', JSON.stringify(modifiedCardParams));

    // formData.append('cardParams', JSON.stringify(cardParams));
formData.append('imgBase64', base64);
    
    // C. Write the edit mode ID to payload
    formData.append('editmode', editConfig?.editMode || '');

    xhr.send(formData);

    return () => {
      if (xhrRef.current) {
        xhrRef.current.abort();
      }
    };
  }, [isOpen, base64, editConfig]);

  if (!isOpen) return null;

  const shareUrl = uploadedId ? `https://www.hearthcards.net/temp/${uploadedId}.png` : '';
  const halfSizeUrl = uploadedId ? `https://www.hearthcards.net/temp/${uploadedId}_half.png` : '';

  const handleCopyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Instant client-side direct full download
  const downloadFullSize = () => {
    const link = document.createElement('a');
    link.download = `${(cardParams.Title || 'hearthcard').replace(/\s+/g, '-')}.png`;
    link.href = base64;
    link.click();
  };

  // Instant client-side direct half-size downscaling (saves bandwidth + offline support)
  const downloadHalfSize = () => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width / 2;
      canvas.height = img.height / 2;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const link = document.createElement('a');
      link.download = `${(cardParams.Title || 'hearthcard').replace(/\s+/g, '-')}_half.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '480px' }}>
        <div className="modal-content text-bg-dark border-secondary">
          
          <div className="modal-header border-secondary p-3">
            <h5 className="modal-title fs-5 text-warning fw-bold">Card Created Successfully</h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          <div className="modal-body p-3 text-center overflow-hidden">
            {/* Right-click savable static Image */}
            <div className="mb-3 d-flex justify-content-center">
              <img 
                src={base64} 
                alt="Finished Card" 
                className="img-fluid rounded shadow" 
                style={{ maxHeight: '420px', width: 'auto', objectFit: 'contain' }}
              />
            </div>

            {isUploading ? (
              <div className="p-3">
                <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                  <FaSpinner className="fa-spin text-info" />
                  <span className="text-info fw-bold">Uploading to Server... {progress}%</span>
                </div>
                <div className="progress bg-dark border border-secondary" style={{ height: '12px' }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated bg-info" 
                    role="progressbar" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            ) : error ? (
              <div className="alert alert-danger py-2">{error}</div>
            ) : (
              <div className="px-1">
                {/* Sharing URL field */}
                <label className="form-label text-muted d-block text-start mb-1" style={{ fontSize: '0.8rem' }}>Share Link:</label>
                <div className="input-group mb-3">
                  <input 
                    type="text" 
                    readOnly 
                    className="form-control bg-black text-white border-secondary" 
                    value={shareUrl} 
                    onClick={(e) => e.target.select()}
                  />
                  <button className="btn btn-outline-info" onClick={handleCopyLink}>
                    {copied ? <FaCheck className="text-success" /> : <FaCopy />}
                  </button>
                </div>

                {/* Instant Download Triggers */}
                <div className="row g-2">
                  <div className="col-6">
                    <button className="btn btn-outline-light w-100 py-2 d-flex align-items-center justify-content-center gap-2" onClick={downloadHalfSize}>
                      <FaDownload /> Half Size
                    </button>
                  </div>
                  <div className="col-6">
                    <button className="btn btn-primary w-100 py-2 d-flex align-items-center justify-content-center gap-2" onClick={downloadFullSize}>
                      <FaDownload /> Full Size
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}